import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Icon from '../../ui/Icon';
import { T } from '../../constants/mockData';
import { masterAPI, ticketsAPI } from '../../services/api';

const DROPDOWN_W = 210;
const DROPDOWN_MAX_H = 260;

export default function InlineMasterDropdown({
  ticketId,
  fieldType,   // 'department' | 'category' | 'sub_category'
  apiField,    // DB column name: 'department' | 'category' | 'sub_category'
  value,       // current value (string)
  onChange,    // (newValue) => void – update local state
  parentValue, // optional: for sub_category, filter by parent category
  disabled,    // if true, button is not clickable (e.g. sub_category before category selected)
}) {
  const [open, setOpen]           = useState(false);
  const [options, setOptions]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [portalStyle, setPortalStyle] = useState({});
  const btnRef  = useRef(null);
  const dropRef = useRef(null);

  // Load options once on mount (and when parentValue changes for sub_category)
  useEffect(() => {
    setLoading(true);
    masterAPI.list(fieldType)
      .then(rows => {
        const active = (rows || []).filter(r => r.is_active !== false);
        if (parentValue) {
          setOptions(active.filter(r => !r.parent_value || r.parent_value === parentValue));
        } else {
          setOptions(active);
        }
      })
      .catch(err => console.error('InlineMasterDropdown load error:', err))
      .finally(() => setLoading(false));
  }, [fieldType, parentValue]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (btnRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = useCallback(() => {
    if (open) { setOpen(false); return; }
    if (!btnRef.current) { setOpen(true); return; }

    const rect    = btnRef.current.getBoundingClientRect();
    const scrollX = window.scrollX || 0;
    const scrollY = window.scrollY || 0;
    const PAD     = 8;

    // Horizontal: prefer aligning to left of button
    const spaceRight = window.innerWidth - rect.left;
    const left = spaceRight >= DROPDOWN_W + PAD
      ? rect.left + scrollX
      : rect.right + scrollX - DROPDOWN_W;

    // Vertical: prefer below
    const spaceBelow = window.innerHeight - rect.bottom - PAD;
    const top = spaceBelow >= DROPDOWN_MAX_H
      ? rect.bottom + scrollY + 4
      : rect.top + scrollY - DROPDOWN_MAX_H - 4;

    setPortalStyle({
      position: 'absolute',
      top,
      left,
      width: DROPDOWN_W,
      zIndex: 9999,
    });
    setOpen(true);
  }, [open]);

  const handleSelect = useCallback(async (newValue) => {
    setOpen(false);
    if (newValue === (value || '')) return;
    setSaving(true);
    try {
      await ticketsAPI.update(ticketId, { [apiField]: newValue || null });
      onChange(newValue);
    } catch (err) {
      console.error('InlineMasterDropdown save error:', err);
    } finally {
      setSaving(false);
    }
  }, [ticketId, apiField, value, onChange]);

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={disabled ? undefined : handleOpen}
        disabled={saving || disabled}
        title={disabled ? 'Select a category first' : undefined}
        style={{
          padding: '4px 8px',
          borderRadius: 6,
          border: `1px solid ${disabled ? T.border : open ? T.active : T.border}`,
          background: disabled ? T.g50 : open ? T.g100 : T.g50,
          color: disabled ? T.muted : value ? '#1A202C' : T.muted,
          fontSize: 12,
          fontWeight: value ? 500 : 400,
          cursor: disabled ? 'not-allowed' : saving ? 'wait' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          whiteSpace: 'nowrap',
          minWidth: 80,
          maxWidth: 160,
          transition: 'all 0.15s',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
          {saving ? '…' : (value || '—')}
        </span>
        <Icon name="chevronD" size={8} color={T.muted} />
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={dropRef}
          style={{
            ...portalStyle,
            background: '#FFFFFF',
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
            maxHeight: DROPDOWN_MAX_H,
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <div style={{ padding: '10px 14px', fontSize: 12, color: T.muted }}>Loading…</div>
          ) : (
            <>
              {/* Clear / None option */}
              <button
                onClick={() => handleSelect('')}
                style={{
                  width: '100%',
                  padding: '8px 14px',
                  border: 'none',
                  borderBottom: `1px solid ${T.border}`,
                  background: !value ? T.g50 : 'transparent',
                  color: T.muted,
                  textAlign: 'left',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                — None
              </button>
              {options.length === 0 ? (
                <div style={{ padding: '10px 14px', fontSize: 12, color: T.muted }}>No options</div>
              ) : (
                options.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => handleSelect(opt.value)}
                    style={{
                      width: '100%',
                      padding: '8px 14px',
                      border: 'none',
                      background: value === opt.value ? T.g50 : 'transparent',
                      color: '#1A202C',
                      textAlign: 'left',
                      fontSize: 12,
                      fontWeight: value === opt.value ? 600 : 400,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (value !== opt.value) e.currentTarget.style.background = T.g50; }}
                    onMouseLeave={e => { if (value !== opt.value) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {opt.color && (
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: opt.color, flexShrink: 0,
                      }} />
                    )}
                    {opt.value}
                  </button>
                ))
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
